/**
 * PackageEditor — the Package column while editing: package select ("No
 * package" first), a cancel button, and the instant-apply confirm for the
 * package picked. Presentational: useLibraryPackageCell owns the state and
 * the writes.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { X } from 'lucide-react';
import type { FlowOptimizerPackage } from '@trafficpulse/shared';

import { Button, SearchableSelect } from '@/components/ui';
import AssignConfirmDialog, {
  type AssignConfirmDialogProps,
} from '@/features/flow-optimizer-library/scheduler/AssignConfirmDialog';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';

export const NO_PACKAGE = '';

export interface PackageEditorProps {
  row: OptimizerRow;
  packages: FlowOptimizerPackage[];
  /** The package picked and awaiting its confirm. */
  pending: FlowOptimizerPackage | null;
  busy: boolean;
  onPick: (packageId: string) => void;
  onConfirm: () => void;
  onDismissPending: () => void;
  onClose: () => void;
}

function packageOptions(packages: FlowOptimizerPackage[]) {
  return [
    { value: NO_PACKAGE, label: 'No package' },
    ...packages.map((pkg) => ({ value: pkg.id, label: pkg.name })),
  ];
}

/** The instant-apply confirm for the package picked (closed while none is). */
function confirmProps(props: PackageEditorProps): AssignConfirmDialogProps {
  const { row, pending } = props;
  return {
    open: pending !== null,
    packageName: pending?.name ?? '',
    formulaSetName: pending?.formulaSetName,
    ruleSetName: pending?.ruleSetName,
    campaignName: row.campaignName,
    flipsStandard: row.mode === 'STANDARD',
    busy: props.busy,
    onConfirm: props.onConfirm,
    onCancel: props.onDismissPending,
  };
}

export const PackageEditor: FC<PackageEditorProps> = (props) => (
  <span className="inline-flex items-center gap-1">
    <span className="w-44">
      <SearchableSelect
        options={packageOptions(props.packages)}
        value={props.row.package?.id ?? NO_PACKAGE}
        onChange={props.onPick}
        placeholder="Select package…"
      />
    </span>
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6"
      aria-label="Cancel package edit"
      onClick={props.onClose}
    >
      <X className="h-3.5 w-3.5" />
    </Button>
    <AssignConfirmDialog {...confirmProps(props)} />
  </span>
);

export default PackageEditor;
